import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Pencil, Search, Activity, Code, Clock, ChevronUp, ChevronDown, Wifi, WifiOff, Bug, BugOff } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { EditDeviceDetails } from "./EditDeviceDetails";
import { DeviceDetails } from "./DeviceDetails";
import { useDeviceEdit } from "@/hooks/use-device-edit";
import { useVisibilityRefresh } from "@/hooks/useVisibilityRefresh";
import { useIsMobile } from "@/hooks/use-mobile";

type DeviceWithContract = {
  device_id: string;
  device_name: string | null;
  device_description: string | null;
  device_firmware: string | null;
  device_status: string | null;
  device_last_updated: string | null;
  device_serial: string | null;
  images_allowed: number;
  images_current: number;
  notification_ai_model?: string | null;
  camera_image_quality?: string | null;
  group_name?: string | null;
  group_reference?: string | null;
  camera_config?: {
    enableDebug?: number;
  } | null;
  imagekit?: {
    url?: string | null;
    thumbnailUrl?: string | null;
  } | null;
};

type SortDirection = 'asc' | 'desc' | null;

// Define the ref type for the component
export type DeviceTableRefType = {
  refetch: () => Promise<void>;
  refreshAllDevices: () => Promise<void>;
};

export const DeviceDetailsTable = forwardRef<DeviceTableRefType, {}>((_, ref) => {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [editingDevice, setEditingDevice] = useState<DeviceWithContract | null>(null);
  const [viewingDeviceId, setViewingDeviceId] = useState<string | null>(null);
  const [hasAiModelColumn, setHasAiModelColumn] = useState(false);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [lastRealtimeUpdate, setLastRealtimeUpdate] = useState<Date | null>(null);
  const isMobile = useIsMobile();

  const { updateDeviceDetails } = useDeviceEdit();
  const { trackChannel } = useVisibilityRefresh();

  // Set up real-time subscription for all device_details changes
  useEffect(() => {
    console.log('Setting up realtime subscription for device_details table');
    
    const channel = supabase
      .channel(`device-details-table-${Date.now()}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'device_details'
      }, (payload) => {
        console.log('Realtime update received for device table:', payload);
        setLastRealtimeUpdate(new Date());
        
        // Invalidate the main query to refetch data
        queryClient.invalidateQueries({ queryKey: ['device-details-table'] });
      })
      .subscribe((status) => {
        console.log('Device table realtime subscription status:', status);
        
        // Re-establish connection if it drops
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.log('Real-time table connection lost, attempting to reconnect...');
          setTimeout(() => {
            channel.unsubscribe().then(() => {
              queryClient.invalidateQueries({ queryKey: ['device-details-table'] });
            });
          }, 1000);
        }
      });
    
    // Track the channel for visibility-based reconnection
    trackChannel(channel);

    return () => {
      console.log('Cleaning up device table realtime subscription');
      // Properly unsubscribe before removing channel
      channel.unsubscribe().then(() => {
        supabase.removeChannel(channel);
      });
    };
  }, [queryClient]);

  // Fetch device details from database only - no more API calls
  const { data: deviceDetails, isLoading, error, refetch } = useQuery({
    queryKey: ['device-details-table'],
    queryFn: async () => {
      console.log("Fetching device details from database");
      try {
        // First check if the notification_ai_model column exists
        try {
          const { data: checkData, error: checkError } = await supabase
            .from("device_details")
            .select("notification_ai_model")
            .limit(1);
          
          setHasAiModelColumn(!checkError);
          
          // If column exists, include it in the query
          if (!checkError) {
            const { data, error } = await supabase.from("device_details").select(`
              device_id,
              device_name,
              device_description,
              device_firmware,
              device_status,
              device_last_updated,
              device_serial,
              images_allowed,
              images_current,
              notification_ai_model,
              camera_image_quality,
              group_name,
              group_reference,
              camera_config,
              imagekit
            `);
            
            if (error) {
              console.error("Error fetching device details:", error);
              toast.error("Failed to fetch device details");
              return [] as DeviceWithContract[];
            }
            
            console.log("Fetched data with AI model:", data);
            return (data as unknown as DeviceWithContract[]) || [];
          }
        } catch (checkError) {
          console.log("Error checking for notification_ai_model column:", checkError);
          setHasAiModelColumn(false);
        }
        
        // If the check failed or column doesn't exist, fetch without it
        const { data, error } = await supabase.from("device_details").select(`
          device_id,
          device_name,
          device_description,
          device_firmware,
          device_status,
          device_last_updated,
          device_serial,
          images_allowed,
          images_current,
          group_name,
          group_reference,
          camera_config,
          imagekit
        `);
        
        if (error) {
          console.error("Error fetching device details:", error);
          toast.error("Failed to fetch device details");
          return [] as DeviceWithContract[];
        }
        
        console.log("Fetched data without AI model:", data);
        return (data as unknown as DeviceWithContract[]) || [];
      } catch (error) {
        console.error("Error in device details query:", error);
        toast.error("Failed to fetch device details");
        return [] as DeviceWithContract[];
      }
    },
    staleTime: 60 * 1000, // 60 seconds - allow data to become stale
    refetchOnWindowFocus: true, // Enable refetch when tab becomes active
  });

  // Database refresh only - instant, no API calls
  const refreshDatabase = async (): Promise<void> => {
    console.log('Refreshing database data (no API calls)');
    await refetch();
    toast.success("Device data refreshed from database");
  };

  // Expose refetch methods through ref
  useImperativeHandle(ref, () => ({
    refetch: refreshDatabase,
    refreshAllDevices: refreshDatabase // Remove bulk API calls completely
  }), [refetch]);

  // Ensure deviceDetails is always an array to prevent TypeScript errors
  const devicesArray = Array.isArray(deviceDetails) ? deviceDetails : [];

  const filteredDevices = devicesArray.filter(device => {
    // Exclude devices with Demo status
    if (device.device_status?.toLowerCase() === 'demo') {
      return false;
    }
    
    const searchLower = searchQuery.toLowerCase();
    return (device.device_serial?.toLowerCase() || "").includes(searchLower) || 
           (device.device_name?.toLowerCase() || "").includes(searchLower) || 
           (device.device_firmware?.toLowerCase() || "").includes(searchLower) || 
           (device.device_status?.toLowerCase() || "").includes(searchLower);
  });

  // Sort devices by serial number
  const sortedDevices = useMemo(() => {
    if (!sortDirection) return filteredDevices;
    
    return [...filteredDevices].sort((a, b) => {
      const serialA = a.device_serial?.toLowerCase() || '';
      const serialB = b.device_serial?.toLowerCase() || '';
      
      if (sortDirection === 'asc') {
        return serialA.localeCompare(serialB);
      } else {
        return serialB.localeCompare(serialA);
      }
    });
  }, [filteredDevices, sortDirection]);

  const toggleSort = () => {
    if (sortDirection === null) {
      setSortDirection('asc');
    } else if (sortDirection === 'asc') {
      setSortDirection('desc');
    } else {
      setSortDirection(null);
    }
  };

  const handleEdit = async () => {
    if (!editingDevice) return;
    const success = await updateDeviceDetails({
      device_id: editingDevice.device_id,
      device_name: editingDevice.device_name,
      device_description: editingDevice.device_description,
      images_allowed: editingDevice.images_allowed,
      images_current: editingDevice.images_current,
      camera_image_quality: editingDevice.camera_image_quality,
      group_name: editingDevice.group_name,
      group_reference: editingDevice.group_reference
    });
    if (success) {
      setEditingDevice(null);
      refetch();
    }
  };

  const renderDataFreshnessIndicator = () => {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        {lastRealtimeUpdate ? (
          <>
            <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-200">
              <Wifi className="w-3 h-3 mr-1" />
              Live
            </Badge>
            <span>Updated {formatDistanceToNow(lastRealtimeUpdate)} ago</span>
          </>
        ) : (
          <>
            <Badge variant="outline" className="border-yellow-200 text-yellow-700">
              <WifiOff className="w-3 h-3 mr-1" />
              Database
            </Badge>
            <span className="hidden md:inline">Real-time updates enabled</span>
          </>
        )}
      </div>
    );
  };

  if (isLoading) {
    return <div className="flex justify-center p-8">Loading device details...</div>;
  }

  if (error) {
    return <div className="bg-destructive/10 p-4 rounded-md text-destructive">Error loading devices. Please try refreshing the page.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search devices..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64"
            />
          </div>
          <div className="flex items-center gap-4">
            {renderDataFreshnessIndicator()}
            <Button 
              onClick={refreshDatabase}
              className="flex items-center gap-2 bg-[#DFEF97] text-black hover:bg-[#DFEF97]/90"
            >
              <Activity className="h-4 w-4" />
              Sync Latest
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <button 
                  onClick={toggleSort} 
                  className="flex items-center gap-2 hover:text-primary transition-colors"
                >
                  Serial Number
                  {sortDirection === 'asc' && <ChevronUp className="h-4 w-4" />}
                  {sortDirection === 'desc' && <ChevronDown className="h-4 w-4" />}
                </button>
              </TableHead>
              <TableHead className="hidden md:table-cell">Name</TableHead>
              <TableHead className="hidden md:table-cell whitespace-nowrap text-center">
                <div className="flex items-center justify-center gap-2">
                  <Code className="h-4 w-4" />
                  Firmware
                </div>
              </TableHead>
              <TableHead className="hidden md:table-cell text-center">
                <div className="flex items-center justify-center gap-2">
                  <Activity className="h-4 w-4" />
                  Status
                </div>
              </TableHead>
              <TableHead className="text-center">
                <div className="flex items-center justify-center gap-2">
                  <Clock className="h-4 w-4" />
                  Last Updated
                </div>
              </TableHead>
              <TableHead className="hidden md:table-cell text-center">Images Allowed</TableHead>
              <TableHead className="hidden md:table-cell text-center">Images Used</TableHead>
              <TableHead className="hidden md:table-cell text-center">
                <div className="flex items-center justify-center gap-2">
                  <Bug className="h-4 w-4" />
                  Debug
                </div>
              </TableHead>
              {hasAiModelColumn && <TableHead className="hidden md:table-cell text-center">AI Model</TableHead>}
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedDevices.map(device => (
              <TableRow key={device.device_id}>
                <TableCell>{device.device_serial || "-"}</TableCell>
                <TableCell className="hidden md:table-cell">{device.device_name || "-"}</TableCell>
                <TableCell className="hidden md:table-cell text-center">{device.device_firmware || "-"}</TableCell>
                <TableCell className="hidden md:table-cell text-center">{device.device_status || "-"}</TableCell>
                <TableCell className="text-center">
                  {device.device_last_updated ? formatDistanceToNow(new Date(device.device_last_updated), {
                    addSuffix: true
                  }) : "-"}
                </TableCell>
                <TableCell className="hidden md:table-cell text-center">{device.images_allowed ?? "—"}</TableCell>
                <TableCell className="hidden md:table-cell text-center">{device.images_current ?? "—"}</TableCell>
                <TableCell className="hidden md:table-cell text-center">
                  <div className="flex items-center justify-center">
                    {(() => {
                      try {
                        let cameraConfig = device.camera_config;
                        
                        // Parse camera_config if it's a JSON string
                        if (typeof cameraConfig === 'string') {
                          cameraConfig = JSON.parse(cameraConfig);
                        }
                        
                        // Check for debug enabled (handle both number 1 and string "1")
                        const enableDebug = cameraConfig?.enableDebug;
                        const isDebugEnabled = (typeof enableDebug === 'number' && enableDebug === 1) || 
                                             (typeof enableDebug === 'string' && enableDebug === '1');
                        
                        return isDebugEnabled ? (
                          <div title="Debug enabled">
                            <Bug className="h-4 w-4 text-orange-500" />
                          </div>
                        ) : (
                          <div title="Debug disabled">
                            <BugOff className="h-4 w-4 text-gray-400" />
                          </div>
                        );
                      } catch (error) {
                        // If JSON parsing fails or any other error, default to debug disabled
                        return (
                          <div title="Debug disabled">
                            <BugOff className="h-4 w-4 text-gray-400" />
                          </div>
                        );
                      }
                    })()}
                  </div>
                </TableCell>
                {hasAiModelColumn && <TableCell className="hidden md:table-cell text-center">{device.notification_ai_model || "-"}</TableCell>}
                <TableCell>
                  <Button variant="outline" size="icon" onClick={() => setEditingDevice(device)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editingDevice} onOpenChange={isOpen => !isOpen && setEditingDevice(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Device Details</DialogTitle>
            <DialogDescription>
              Update the device name, description, AI model, and image limits.
            </DialogDescription>
          </DialogHeader>
          {editingDevice && (
            <EditDeviceDetails 
              deviceId={editingDevice.device_id}
              initialName={editingDevice.device_name}
              initialDescription={editingDevice.device_description}
              initialCameraImageQuality={editingDevice.camera_image_quality}
              initialImagesAllowed={editingDevice.images_allowed || 30}
              initialImagesCurrent={editingDevice.images_current || 0}
              initialGroupName={editingDevice.group_name}
              initialGroupReference={editingDevice.group_reference}
              imageUrl={editingDevice.imagekit?.url}
              thumbnailUrl={editingDevice.imagekit?.thumbnailUrl}
              onSave={() => {
                refetch();
                setEditingDevice(null);
              }}
              onCancel={() => setEditingDevice(null)}
              onViewFullDetails={() => {
                setViewingDeviceId(editingDevice.device_id);
                setEditingDevice(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Full Device Details Dialog/Sheet */}
      {isMobile ? (
        <Sheet open={!!viewingDeviceId} onOpenChange={(open) => !open && setViewingDeviceId(null)}>
          <SheetContent side="right" className="p-3 w-full sm:max-w-[85vw] overflow-hidden">
            <ScrollArea className="h-full w-full max-w-full overflow-x-hidden">
              {viewingDeviceId && (
                <DeviceDetails deviceId={viewingDeviceId} />
              )}
            </ScrollArea>
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={!!viewingDeviceId} onOpenChange={(open) => !open && setViewingDeviceId(null)}>
          <DialogContent className="w-[90vw] h-[80vh] max-w-[80vw] max-h-[80vh] p-0 overflow-hidden">
            <ScrollArea className="h-full w-full max-w-full overflow-x-hidden">
              {viewingDeviceId && (
                <DeviceDetails deviceId={viewingDeviceId} />
              )}
            </ScrollArea>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
});

// Add display name for the forwarded ref component
DeviceDetailsTable.displayName = "DeviceDetailsTable";